import React, { useCallback } from 'react';
import type { FC } from 'react';
import { Box, SelectInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { match, lookup } from 'variant';
import { CloseScreen } from '../CloseScreen/CloseScreen.js';
import type { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';
import {
  useOpenPage,
  useResolveDashboardExtensionDevUrl,
} from '../extensions/utils.js';
import { useExtensions } from '../extensions/useExtensions.js';
import type { DashboardExtension } from '../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../bi/index.js';
import { useExtensionData } from '../../context/extensionData.js';

interface Props {
  dashboardExtension: DashboardExtension;
  onSelect?: (page: DevCenterComponent<DashboardExtension>) => void;
  onClose: () => void;
}

export const DashboardArrowViewer: FC<Props> = ({
  dashboardExtension,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { getExtensions } = useExtensions();
  const bi = useWixCliAppBi();
  const resolveDashboardExtensionUrl = useResolveDashboardExtensionDevUrl();
  const { getDashboardExtensionsRoute } = useExtensionData();
  const openPage = useOpenPage();

  const getLabelKey = useCallback(
    () =>
      match(
        dashboardExtension,
        lookup({
          DashboardPlugin:
            'dev_command.dashboard_page_plugin_screen.select_label',
          DashboardMenuPlugin:
            'dev_command.dashboard_menu_plugin_screen.select_label',
          DashboardPage: 'dev_command.dashboard_pages_screen.select_label',
          DashboardModal: 'dev_command.dashboard_modal_screen.select_label',
        })
      ),
    [dashboardExtension]
  );

  const handleSubmit = useCallback(
    async (dashboardExtension: DevCenterComponent<DashboardExtension>) => {
      const url = resolveDashboardExtensionUrl(dashboardExtension);
      bi.cliAction({
        question: t(getLabelKey()),
        questionKey: getLabelKey(),
        action: dashboardExtension.title,
        key: 'enter',
      });
      await openPage({ url, title: dashboardExtension.title });
      onSelect?.(dashboardExtension);
    },
    [resolveDashboardExtensionUrl, bi, t, getLabelKey, openPage, onSelect]
  );

  return (
    <Box flexDirection="column" gap={1}>
      <Box flexDirection="column">
        <SelectInput<DevCenterComponent<DashboardExtension>>
          label={t(getLabelKey())}
          options={getExtensions(dashboardExtension).map(
            (dashboardExtensionInstance) => ({
              key: dashboardExtensionInstance.id,
              title: dashboardExtensionInstance.title,
              description: getDashboardExtensionsRoute(
                dashboardExtensionInstance
              ),
              value: dashboardExtensionInstance,
            })
          )}
          onSubmit={({ value: dashboardExtensionInstance }) =>
            handleSubmit(dashboardExtensionInstance)
          }
        />
      </Box>
      <CloseScreen onClose={onClose} />
    </Box>
  );
};
